/**
 * The back button during a sit.
 *
 * The app is one document with no URLs, so the Android back button had nothing to go back
 * to: it left for the phone's home screen, in the middle of a sit, with one press. There
 * are two routes to fixing that, because the button reaches the page two different ways.
 *
 * In Chrome, the installed PWA and the TWA, back is `history.back()` whenever the page has
 * an entry to go back to. So the sit pushes one entry of its own, and a press lands on a
 * `popstate` the app can answer with a question. The entry is pushed again as soon as it
 * is popped, so a second press asks again rather than falling through to the exit the
 * first was stopped from making; and when the sit ends by any other route the entry is
 * taken back off, or the next press on the home screen would spend itself on a dead entry
 * and appear to do nothing.
 *
 * The native shell's back press never reaches the WebView's history: Capacitor's
 * BridgeActivity does not handle it, so Android finishes the task. MainActivity asks the
 * page instead, by calling `window.brahmBackButton()`, and takes `true` to mean the page
 * has dealt with it. That route does not touch history at all, which also keeps it clear
 * of Chrome's intervention against entries pushed without a fresh user gesture.
 */

const MARK = 'brahm-sit';

interface HistoryLike {
  readonly state: unknown;
  pushState(data: unknown, unused: string): void;
  back(): void;
}

interface WindowLike {
  history: HistoryLike;
  addEventListener(type: 'popstate', listener: () => void): void;
  removeEventListener(type: 'popstate', listener: () => void): void;
}

/** The name MainActivity.java calls. Change both or neither. */
export const NATIVE_BACK_HOOK = 'brahmBackButton';

type NativeHost = { [NATIVE_BACK_HOOK]?: () => boolean };

const isMark = (state: unknown) =>
  typeof state === 'object' && state !== null && (state as { guard?: unknown }).guard === MARK;

/** Holds the back button through history while installed. Returns the release. */
export function guardBack(win: WindowLike, onBack: () => void): () => void {
  win.history.pushState({ guard: MARK }, '');
  const onPop = () => {
    win.history.pushState({ guard: MARK }, '');
    onBack();
  };
  win.addEventListener('popstate', onPop);
  return () => {
    win.removeEventListener('popstate', onPop);
    if (isMark(win.history.state)) win.history.back();
  };
}

/**
 * Holds the native shell's back button while installed. Returns the release, after which
 * the hook is gone and MainActivity lets the press do what Android would have done.
 */
export function guardNativeBack(target: object, onBack: () => void): () => void {
  const host = target as NativeHost;
  const hook = () => { onBack(); return true; };
  host[NATIVE_BACK_HOOK] = hook;
  return () => {
    if (host[NATIVE_BACK_HOOK] === hook) delete host[NATIVE_BACK_HOOK];
  };
}
